import type { Game, RoomState } from '../../shared/types.js';

interface Slice {
  gameId: string;
  count: number;
  // Cached image element if available
  img?: HTMLImageElement;
  game: Game;
}

const imageCache = new Map<string, HTMLImageElement>();
function getImage(url: string): HTMLImageElement {
  let img = imageCache.get(url);
  if (img) return img;
  img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = url;
  imageCache.set(url, img);
  return img;
}

export class Wheel {
  private rotation = 0; // current rotation in degrees
  private animFrame: number | null = null;
  private animStart = 0;
  private animFrom = 0;
  private animTo = 0;
  private animDuration = 0;
  private slices: Slice[] = [];
  private games: Map<string, Game> = new Map();
  private dpr = window.devicePixelRatio || 1;

  constructor(private canvas: HTMLCanvasElement) {
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.draw();
  }

  setState(state: RoomState): void {
    this.games = new Map(state.games.map((g) => [g.id, g]));
    this.slices = state.slices
      .map((s): Slice | null => {
        const g = this.games.get(s.gameId);
        if (!g) return null;
        return {
          gameId: s.gameId,
          count: s.count,
          game: g,
          img: g.logoUrl ? getImage(g.logoUrl) : undefined,
        };
      })
      .filter((x): x is Slice => x !== null);
    this.draw();
  }

  // Snap to a particular rotation immediately (used to reset between rounds).
  reset(): void {
    if (this.animFrame !== null) cancelAnimationFrame(this.animFrame);
    this.animFrame = null;
    this.rotation = 0;
    this.draw();
  }

  spinTo(finalRotation: number, durationMs: number, startedAt: number): void {
    if (this.animFrame !== null) cancelAnimationFrame(this.animFrame);
    this.animFrom = this.rotation;
    this.animTo = finalRotation;
    this.animDuration = durationMs;
    this.animStart = startedAt;
    const tick = () => {
      const now = Date.now();
      const t = Math.max(0, Math.min(1, (now - this.animStart) / this.animDuration));
      // Strong ease-out so it slows toward the end.
      const eased = 1 - Math.pow(1 - t, 4);
      this.rotation = this.animFrom + (this.animTo - this.animFrom) * eased;
      this.draw();
      if (t < 1) this.animFrame = requestAnimationFrame(tick);
      else this.animFrame = null;
    };
    this.animFrame = requestAnimationFrame(tick);
  }

  private totalCount(): number {
    return this.slices.reduce((s, x) => s + x.count, 0);
  }

  private draw(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (this.slices.length === 0) {
      ctx.save();
      ctx.fillStyle = '#1a1d24';
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, Math.min(w, h) / 2 - 6 * this.dpr, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#888';
      ctx.font = `${16 * this.dpr}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No picks yet', w / 2, h / 2);
      ctx.restore();
      return;
    }

    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(w, h) / 2 - 6 * this.dpr;
    const total = this.totalCount();
    const rotRad = (this.rotation * Math.PI) / 180;

    // Pointer is at the top (12 o'clock). With rotation = 0, slice 0 starts there.
    // Wheel rotates clockwise as rotation grows.
    let startAngle = -Math.PI / 2 + rotRad;
    for (const slice of this.slices) {
      const sliceAngle = (slice.count / total) * Math.PI * 2;
      const endAngle = startAngle + sliceAngle;

      // Slice fill
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = colorFor(slice.game);
      ctx.fill();

      // Slice border
      ctx.strokeStyle = '#0f1115';
      ctx.lineWidth = 2 * this.dpr;
      ctx.stroke();

      // Logo / label inside slice
      const midAngle = startAngle + sliceAngle / 2;
      const logoR = radius * 0.65;
      const lx = cx + Math.cos(midAngle) * logoR;
      const ly = cy + Math.sin(midAngle) * logoR;
      const drawSize = Math.min(radius * 0.4, 90 * this.dpr);

      ctx.save();
      ctx.translate(lx, ly);
      // Always upright. Earlier we tried to keep labels facing the center
      // and flip them on the bottom half, but the conditional flip caused a
      // visible jump as the wheel rotated past the threshold during a spin.
      // Translating the icon to the slice center without rotating it gives a
      // smooth orbit and reads cleanly when the wheel stops.

      const img = slice.img;
      if (img && img.complete && img.naturalWidth > 0) {
        const ratio = img.naturalWidth / img.naturalHeight;
        let dw = drawSize;
        let dh = drawSize;
        if (ratio > 1) dh = drawSize / ratio;
        else dw = drawSize * ratio;
        ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      } else {
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${14 * this.dpr}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        wrapText(ctx, slice.game.name, 0, 0, drawSize * 1.6, 18 * this.dpr);
      }
      ctx.restore();

      // Weight badge for slices with count > 1
      if (slice.count > 1) {
        const badgeR = radius * 0.92;
        const bx = cx + Math.cos(midAngle) * badgeR;
        const by = cy + Math.sin(midAngle) * badgeR;
        ctx.save();
        ctx.fillStyle = '#0f1115';
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2 * this.dpr;
        ctx.beginPath();
        ctx.arc(bx, by, 14 * this.dpr, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${12 * this.dpr}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`×${slice.count}`, bx, by);
        ctx.restore();
      }

      startAngle = endAngle;
    }

    // Hub
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.12, 0, Math.PI * 2);
    ctx.fillStyle = '#0f1115';
    ctx.fill();
    ctx.lineWidth = 3 * this.dpr;
    ctx.strokeStyle = '#fff';
    ctx.stroke();

    // Pointer at the top
    const pSize = 18 * this.dpr;
    ctx.beginPath();
    ctx.moveTo(cx, cy - radius - pSize * 0.2);
    ctx.lineTo(cx - pSize * 0.6, cy - radius - pSize * 1.2);
    ctx.lineTo(cx + pSize * 0.6, cy - radius - pSize * 1.2);
    ctx.closePath();
    ctx.fillStyle = '#ff3355';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2 * this.dpr;
    ctx.stroke();
  }
}

function colorFor(game: Game): string {
  if (game.color) return game.color;
  // Hash-based color when no explicit color is set
  let h = 0;
  for (let i = 0; i < game.id.length; i++) h = (h * 31 + game.id.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 55% 45%)`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((ln, i) => ctx.fillText(ln, x, startY + i * lineHeight));
}
